import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Schreiben an einzelne, mehrere oder alle Eigentümer (WEG: Serienbrief oder Rundschreiben) — öffnet den bestehenden Editor
export default function OwnerLetterDialog({ open, onOpenChange, target, attachmentDocIds = [] }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [mode, setMode] = useState("serial");

  useEffect(() => {
    if (!open) return;
    setData(null);
    api.get("/communications/owner-recipients", { params: target }).then(({ data: d }) => {
      setData(d);
      const pre = d.recipients.filter((r) => !d.is_weg || r.is_unit_of_tenancy);
      setSel((pre.length ? pre : d.recipients).map((r) => r.key));
    }).catch((e) => { toast.error(e?.response?.data?.detail || "Eigentümer konnten nicht geladen werden"); onOpenChange(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const recips = (data?.recipients || []).filter((r) => sel.includes(r.key));
  const toggle = (k) => setSel((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));

  const go = () => {
    if (!recips.length) { toast.error("Bitte mindestens einen Eigentümer wählen"); return; }
    const first = recips[0];
    const context = { contact_id: first.contact_id, property_id: data.property_id, contact_role: "owner" };
    if (first.unit_id && recips.length === 1) context.unit_id = first.unit_id;
    if (target.vorgang_id) context.vorgang_id = target.vorgang_id;
    if (target.tenancy_id) context.journal_tenancy_id = target.tenancy_id;
    window.dispatchEvent(new CustomEvent("domivus:open-composer", { detail: {
      context,
      recipients: recips.map((r) => ({ contact_id: r.contact_id, unit_id: r.unit_id, property_id: data.property_id,
        contact_role: "owner", name: r.name, key: r.key, unit_label: r.unit_label })),
      rundschreiben: recips.length > 1,
      rundschreibenMode: mode === "serial" ? "per_empfaenger" : "sammelbrief",
      attachmentDocIds,
    } }));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="owner-letter-dialog">
        <DialogHeader>
          <DialogTitle>An Eigentümer schreiben</DialogTitle>
          <DialogDescription>{data?.property_name}{data?.is_weg ? " · WEG – alle Wohnungseigentümer" : ""}</DialogDescription>
        </DialogHeader>
        {!data ? <div className="text-sm text-slate-500">Lade Eigentümer…</div> : (
          <div className="space-y-3">
            <div className="flex gap-3 text-xs">
              <button type="button" className="underline" onClick={() => setSel(data.recipients.map((r) => r.key))} data-testid="owner-letter-all">Alle auswählen</button>
              <button type="button" className="underline" onClick={() => setSel([])} data-testid="owner-letter-none">Keine</button>
              <span className="text-slate-500 ml-auto">{recips.length} von {data.recipients.length} gewählt</span>
            </div>
            <div className="border rounded-md divide-y max-h-64 overflow-y-auto">
              {data.recipients.length === 0 && <div className="p-3 text-sm text-slate-500">Keine Eigentümer hinterlegt.</div>}
              {data.recipients.map((r) => (
                <label key={r.key} className="flex items-center gap-2 p-2 text-sm cursor-pointer hover:bg-slate-50" data-testid={`owner-letter-row-${r.contact_id}`}>
                  <Checkbox checked={sel.includes(r.key)} onCheckedChange={() => toggle(r.key)} data-testid={`owner-letter-check-${r.key}`} />
                  <span className="font-medium">{r.name}</span>
                  {r.owner_number && <span className="text-[11px] text-slate-400">#{r.owner_number}</span>}
                  <span className="ml-auto text-[11px] text-slate-500">
                    {r.unit_label && `Einheit ${r.unit_label}`}{r.mea && ` · MEA ${r.mea}`}{r.share_percent != null && `${r.share_percent} %`}
                    {!r.has_address && !r.email && <span className="text-red-600"> · keine Anschrift</span>}
                  </span>
                </label>
              ))}
            </div>
            {recips.length > 1 && (
              <RadioGroup value={mode} onValueChange={setMode} className="space-y-1.5">
                <label className="flex items-start gap-2 text-sm cursor-pointer"><RadioGroupItem value="serial" className="mt-0.5" data-testid="owner-letter-mode-serial" />
                  <span><b>Personalisierter Serienbrief</b> — je Eigentümer ein eigenes Schreiben (Anrede, Einheit, MEA)</span></label>
                <label className="flex items-start gap-2 text-sm cursor-pointer"><RadioGroupItem value="circular" className="mt-0.5" data-testid="owner-letter-mode-circular" />
                  <span><b>Rundschreiben</b> — ein gemeinsames Schreiben an alle gewählten Eigentümer</span></label>
              </RadioGroup>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={go} disabled={!data} className="bg-slate-900 hover:bg-slate-800" data-testid="owner-letter-open-editor">Im Editor verfassen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
